"use client";

type Point = { x: number; y: number };

function pointsToString(points: Point[]) {
  return points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
}

const AXIS_LABEL_MAX_CHARS = 14;
const AXIS_LABEL_LINE_HEIGHT = 12;

function wrapAxisLabel(text: string, maxChars = AXIS_LABEL_MAX_CHARS) {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function getRadarPoints(values: number[], scale: number) {
  const n = values.length;
  const angles = values.map((_, i) => (Math.PI * 2 * i) / n - Math.PI / 2);
  return values.map((v, i) => {
    const r = (v / 3) * scale;
    return { x: Math.cos(angles[i]!) * r, y: Math.sin(angles[i]!) * r };
  });
}

const SCALE = 85;

export function RadarChart({
  values,
  size = 260,
  axisLabels,
}: {
  values: number[];
  size?: number;
  axisLabels?: string[];
}) {
  const n = values.length;
  const pts = getRadarPoints(values, SCALE);
  const outerPts = getRadarPoints(new Array(n).fill(3), SCALE);
  const view = size;
  const c = view / 2;
  const mx = 34;
  const my = 8;

  const effectiveAxisLabels =
    axisLabels && axisLabels.length === n
      ? axisLabels
      : Array.from({ length: n }, (_, i) => `Axis ${i + 1}`);

  const effectiveLabelRadius = SCALE + 26;
  const angles = Array.from({ length: n }, (_, i) => (Math.PI * 2 * i) / n - Math.PI / 2);

  return (
    <svg
      viewBox={`${-mx} ${-my} ${view + mx * 2} ${view + my * 2}`}
      className="w-full h-auto"
    >
      <g transform={`translate(${c},${c})`}>
        <polygon
          points={pointsToString(outerPts)}
          fill="none"
          stroke="var(--bsa-border-strong)"
          strokeWidth="1"
        />
        <polygon
          points={pointsToString(getRadarPoints(new Array(n).fill(2), SCALE))}
          fill="none"
          stroke="var(--bsa-border-strong)"
          strokeWidth="0.7"
          opacity="0.55"
        />
        <polygon
          points={pointsToString(getRadarPoints(new Array(n).fill(1), SCALE))}
          fill="none"
          stroke="var(--bsa-border-strong)"
          strokeWidth="0.7"
          opacity="0.55"
        />

        {outerPts.map((p, i) => (
          <line
            key={i}
            x1={0}
            y1={0}
            x2={p.x}
            y2={p.y}
            stroke="var(--bsa-border-strong)"
            strokeWidth="0.7"
            opacity="0.45"
          />
        ))}

        <polygon
          points={pointsToString(pts)}
          fill="color-mix(in oklab, var(--bsa-canopy-500) 22%, transparent)"
          stroke="var(--bsa-canopy-600)"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />

        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="var(--bsa-canopy-600)" />
        ))}
      </g>

      {effectiveAxisLabels.map((text, i) => {
        const theta = angles[i] ?? 0;
        const cos = Math.cos(theta);
        const textAnchor = cos > 0.35 ? "start" : cos < -0.35 ? "end" : "middle";
        const x = c + cos * effectiveLabelRadius;
        const y = c + Math.sin(theta) * effectiveLabelRadius;

        const lines = wrapAxisLabel(text);

        return (
          <text
            key={`${i}-${text}`}
            x={x}
            y={y}
            textAnchor={textAnchor}
            dominantBaseline="middle"
            style={{ fontSize: "var(--text-xs)", fontWeight: 500 }}
            fill="var(--bsa-primary)"
          >
            {lines.map((line, li) => (
              <tspan
                key={li}
                x={x}
                dy={li === 0 ? -((lines.length - 1) * AXIS_LABEL_LINE_HEIGHT) / 2 : AXIS_LABEL_LINE_HEIGHT}
              >
                {line}
              </tspan>
            ))}
          </text>
        );
      })}
    </svg>
  );
}
